// Catalog of granular permission keys + visibility entities.
// MUST be kept in sync with the SQL functions `permission_keys()` /
// `visibility_entities()` / `role_default_permission()` / `role_default_visibility()`.

export type PermissionKey =
  // CRM
  | "leads.view" | "leads.create" | "leads.edit" | "leads.delete" | "leads.export"
  | "opportunities.view" | "opportunities.create" | "opportunities.edit" | "opportunities.delete" | "opportunities.export"
  | "contacts.view" | "contacts.create" | "contacts.edit" | "contacts.delete" | "contacts.export"
  | "companies.view" | "companies.create" | "companies.edit" | "companies.delete"
  | "tasks.view" | "tasks.create" | "tasks.edit" | "tasks.delete"
  | "pipelines.manage" | "tags.manage" | "loss_reasons.manage" | "custom_fields.manage"
  // Engajamento
  | "campaigns.access" | "campaigns.manage"
  | "cadences.access" | "cadences.manage"
  | "automations.access" | "automations.manage"
  | "audience.access" | "audience.manage"
  // Inbox / WhatsApp
  | "inbox.access" | "whatsapp.conversations.create"
  | "whatsapp.connections.manage" | "whatsapp.templates.manage"
  | "quick_replies.manage"
  // Relatórios / Metas
  | "reports.view" | "reports.export" | "goals.view" | "goals.manage"
  // Admin
  | "users.manage" | "teams.manage" | "billing.access" | "integrations.manage" | "settings.org"
  | "proposals.access" | "proposals.manage"
  | "scripts.access" | "scripts.manage";

export type VisibilityEntity = "leads" | "opportunities" | "contacts" | "companies" | "tasks" | "conversations";
export type VisibilityScope = "own" | "team" | "teams" | "org";
export type OrgRole = "owner" | "admin" | "manager" | "member";

export interface PermissionDef {
  key: PermissionKey;
  label: string;
  description?: string;
}

export interface PermissionGroup {
  id: string;
  label: string;
  permissions: PermissionDef[];
}

export const PERMISSION_GROUPS: PermissionGroup[] = [
  {
    id: "crm",
    label: "CRM",
    permissions: [
      { key: "leads.view", label: "Ver leads" },
      { key: "leads.create", label: "Criar leads" },
      { key: "leads.edit", label: "Editar leads" },
      { key: "leads.delete", label: "Excluir leads" },
      { key: "leads.export", label: "Exportar leads" },
      { key: "opportunities.view", label: "Ver oportunidades" },
      { key: "opportunities.create", label: "Criar oportunidades" },
      { key: "opportunities.edit", label: "Editar oportunidades" },
      { key: "opportunities.delete", label: "Excluir oportunidades" },
      { key: "opportunities.export", label: "Exportar oportunidades" },
      { key: "contacts.view", label: "Ver contatos" },
      { key: "contacts.create", label: "Criar contatos" },
      { key: "contacts.edit", label: "Editar contatos" },
      { key: "contacts.delete", label: "Excluir contatos" },
      { key: "contacts.export", label: "Exportar contatos" },
      { key: "companies.view", label: "Ver empresas" },
      { key: "companies.create", label: "Criar empresas" },
      { key: "companies.edit", label: "Editar empresas" },
      { key: "companies.delete", label: "Excluir empresas" },
      { key: "tasks.view", label: "Ver tarefas" },
      { key: "tasks.create", label: "Criar tarefas" },
      { key: "tasks.edit", label: "Editar tarefas" },
      { key: "tasks.delete", label: "Excluir tarefas" },
      { key: "pipelines.manage", label: "Gerenciar pipelines" },
      { key: "tags.manage", label: "Gerenciar tags" },
      { key: "loss_reasons.manage", label: "Gerenciar motivos de perda" },
      { key: "custom_fields.manage", label: "Gerenciar campos personalizados" },
    ],
  },
  {
    id: "engagement",
    label: "Engajamento",
    permissions: [
      { key: "campaigns.access", label: "Acessar campanhas" },
      { key: "campaigns.manage", label: "Gerenciar campanhas" },
      { key: "cadences.access", label: "Acessar cadências" },
      { key: "cadences.manage", label: "Gerenciar cadências" },
      { key: "automations.access", label: "Acessar automações" },
      { key: "automations.manage", label: "Gerenciar automações" },
      { key: "audience.access", label: "Acessar segmentos" },
      { key: "audience.manage", label: "Gerenciar segmentos" },
    ],
  },
  {
    id: "inbox",
    label: "Inbox / WhatsApp",
    permissions: [
      { key: "inbox.access", label: "Acessar Inbox" },
      { key: "whatsapp.conversations.create", label: "Iniciar novas conversas no WhatsApp" },
      { key: "whatsapp.connections.manage", label: "Gerenciar conexões WhatsApp" },
      { key: "whatsapp.templates.manage", label: "Gerenciar modelos WhatsApp" },
      { key: "quick_replies.manage", label: "Gerenciar respostas rápidas" },
    ],
  },
  {
    id: "reports",
    label: "Relatórios e metas",
    permissions: [
      { key: "reports.view", label: "Ver relatórios" },
      { key: "reports.export", label: "Exportar relatórios" },
      { key: "goals.view", label: "Ver metas" },
      { key: "goals.manage", label: "Gerenciar metas" },
    ],
  },
  {
    id: "tools",
    label: "Ferramentas",
    permissions: [
      { key: "proposals.access", label: "Acessar propostas" },
      { key: "proposals.manage", label: "Gerenciar propostas" },
      { key: "scripts.access", label: "Acessar scripts" },
      { key: "scripts.manage", label: "Gerenciar scripts" },
    ],
  },
  {
    id: "admin",
    label: "Administração",
    permissions: [
      { key: "users.manage", label: "Gerenciar usuários" },
      { key: "teams.manage", label: "Gerenciar equipes" },
      { key: "billing.access", label: "Acessar faturamento" },
      { key: "integrations.manage", label: "Gerenciar integrações" },
      { key: "settings.org", label: "Configurações da organização" },
    ],
  },
];

export const ALL_PERMISSION_KEYS: PermissionKey[] = PERMISSION_GROUPS.flatMap(g =>
  g.permissions.map(p => p.key)
);

export const VISIBILITY_ENTITIES: { key: VisibilityEntity; label: string; supportsTeams?: boolean }[] = [
  { key: "leads", label: "Leads" },
  { key: "opportunities", label: "Oportunidades" },
  { key: "contacts", label: "Contatos" },
  { key: "companies", label: "Empresas" },
  { key: "tasks", label: "Tarefas" },
  { key: "conversations", label: "Conversas (Inbox)", supportsTeams: true },
];

export const VISIBILITY_SCOPES: { key: VisibilityScope; label: string; description: string }[] = [
  { key: "own", label: "Próprios", description: "Apenas registros onde é responsável" },
  { key: "team", label: "Minha equipe", description: "Próprios + colegas das mesmas equipes" },
  { key: "teams", label: "Equipes específicas", description: "Conversas atribuídas a equipes escolhidas" },
  { key: "org", label: "Organização", description: "Todos os registros da organização" },
];

// Defaults — mirror of SQL `role_default_permission`
const MANAGER_DENIED = new Set<PermissionKey>([
  "users.manage", "teams.manage", "billing.access", "integrations.manage", "settings.org",
  "whatsapp.connections.manage", "custom_fields.manage",
]);

const MEMBER_ALLOWED = new Set<PermissionKey>([
  "leads.view", "leads.create", "leads.edit", "leads.export",
  "opportunities.view", "opportunities.create", "opportunities.edit", "opportunities.export",
  "contacts.view", "contacts.create", "contacts.edit",
  "companies.view", "companies.create", "companies.edit",
  "tasks.view", "tasks.create", "tasks.edit", "tasks.delete",
  "inbox.access", "whatsapp.conversations.create", "campaigns.access", "cadences.access", "audience.access",
  "reports.view", "goals.view", "proposals.access", "scripts.access",
]);

export function roleDefaultPermission(role: OrgRole, key: PermissionKey): boolean {
  if (role === "owner" || role === "admin") return true;
  if (role === "manager") return !MANAGER_DENIED.has(key);
  return MEMBER_ALLOWED.has(key);
}

export function roleDefaultVisibility(role: OrgRole): VisibilityScope {
  if (role === "owner" || role === "admin") return "org";
  if (role === "manager") return "team";
  return "own";
}

export function resolvePermission(
  role: OrgRole,
  overrides: Record<string, boolean> | null | undefined,
  key: PermissionKey
): boolean {
  if (overrides && key in overrides) return !!overrides[key];
  return roleDefaultPermission(role, key);
}

export function resolveVisibility(
  role: OrgRole,
  dv: Record<string, VisibilityScope> | null | undefined,
  entity: VisibilityEntity
): VisibilityScope {
  if (dv && entity in dv) return dv[entity];
  return roleDefaultVisibility(role);
}
